/**
 * RWBR — A SEASON IN PROGRESS, SPLIT INTO THINGS A DATABASE CAN HOLD.
 *
 * Howard, 17 Aug 2026:
 *
 *   "Save and load manually is the key to keeping progress straight without
 *    burning up the I/O at supabase on auto saves."
 *
 * `seasonPlay.ts` made a season playable one race at a time and proved that its
 * `SeasonState` survives `JSON.parse(JSON.stringify(...))`. That made saving a
 * half-played season POSSIBLE. It did not make it AFFORDABLE, and this file is
 * about the difference.
 *
 * ===========================================================================
 * THE MEASUREMENT THAT DECIDED THE DESIGN
 * ===========================================================================
 *
 * Measured on the real engine, seed 424242, finance track on (which is how
 * `App.tsx` begins every season), `SeasonState` serialised whole:
 *
 *   after race   raw JSON      gzip      gzip+base64
 *   ----------   ---------   --------   -------------
 *        1         4.27 MB    0.34 MB      0.45 MB
 *        6        24.23 MB    2.01 MB      2.68 MB
 *       12        47.47 MB    3.91 MB      5.21 MB
 *       18        69.30 MB    5.73 MB      7.64 MB
 *       24        90.73 MB    7.52 MB     10.03 MB
 *       30       114.11 MB    9.53 MB     12.71 MB
 *       36       137.89 MB   11.54 MB     15.39 MB
 *
 * A ONE-ROW SAVE WAS REJECTED ON THOSE NUMBERS, for three reasons and not one:
 *
 *   1. THE WIRE. 15.4 MB on every press of Save at the back end of the year,
 *      and 12.7 MB at race 30, from a phone, is not a save button. Worse, the
 *      cost is O(THE WHOLE SEASON): saving race 31 re-uploads races 1-30, which
 *      is exactly the Supabase I/O Howard asked us not to burn.
 *   2. THE MEMORY. `JSON.stringify` of 138 MB is a ~276 MB UTF-16 string, then a
 *      138 MB byte array, then a gzip window on top. Node did it in 2.9 s with
 *      an 8 GB heap. A phone browser does not have that to spend on a save, and
 *      the failure mode is a killed tab, not a slow one.
 *   3. THE CEILING. 15.39 MB stored was already inside touching distance of the
 *      16 MB `MAX_PAYLOAD_BYTES` this project set for a slot at the time. (That
 *      ceiling is 30 MB now — raised for the season archive, not for this — and
 *      the argument is unchanged.) A design whose best case is "just fits" has
 *      no room for a longer schedule, a bigger field, or a seed whose season
 *      runs hot.
 *
 * SO THE SEASON IS SPLIT, and the split is where all the leverage is. Measured
 * the same way, on the same season:
 *
 *   after race   head raw   head gz   ONE race chunk raw   chunk gz
 *   ----------   --------   -------   ------------------   --------
 *        1         46 KB      6 KB          3.31 MB         259 KB
 *       12         53 KB      7 KB          3.33 MB         272 KB
 *       24         57 KB      7 KB          2.58 MB         226 KB
 *       36         65 KB      8 KB          2.44 MB         205 KB
 *
 *   whole 36-race season, stored as head + 36 chunks:  8.50 MB gzipped
 *   the same season stored as one row:                11.54 MB gzipped
 *
 * A save is now the HEAD (8 KB gzipped, always) plus the chunks the server does
 * not already hold — which is ONE race, about 0.3 MB, on every save after the
 * first. That is O(one race), which is what the requirement asked for.
 *
 * THE 26% THAT DISAPPEARED IS NOT DROPPED DATA. `SeasonState` holds three
 * arrays that are, by construction, views of the per-race records:
 *
 *   • `standingsAfterRace[i]` IS `races[i].standings` — `runSeasonRace` pushes
 *     the very same object into both.
 *   • `events` IS the concatenation of `races[i].events` — every `emit` inside
 *     `runSeasonRace` appends the same object to the race's slice AND to the
 *     season stream, and there is no emit that skips either.
 *   • `finance.events` and `finance.sponsorChanges` are likewise the
 *     concatenation of `finance.races[i].events` and `.sponsorChanges` (the
 *     latter with the race number stamped on, which `rebuild` reproduces in the
 *     same key order so the JSON is byte-identical).
 *
 * So they are stored once and rebuilt, and `scripts/slot-resume.ts` MEASURES
 * that the rebuilt state is byte-identical to the state that was split, at
 * seven save points on three seeds, and that playing the rebuilt state out to
 * race 36 produces the same `SeasonResult` as never having saved at all. That
 * property — not "the load did not crash" — is the whole point of this file.
 *
 * ===========================================================================
 * WHAT THIS FILE DOES NOT DO
 * ===========================================================================
 *
 * It does not touch a network, a clock, a random number or a Supabase client,
 * and it never compresses anything. It turns one object into a head and a list
 * of chunks, and turns them back. `leagueSlot.ts` owns the codec, the rows and
 * the statements; keeping the two apart is what makes the risky half — deciding
 * what a saved season IS — testable without a socket.
 *
 * ⚠ THERE IS NO CLOUD AUTOSAVE. The account save is still a button (Howard: the
 * I/O at Supabase). D-EZ-99 adds a LOCAL safety net instead — `localBackup.ts`
 * writes the same head and chunks to this device's IndexedDB after every race,
 * which costs no server I/O at all.
 *
 * D-EZ-99 — THE HEAD NO LONGER CARRIES THE SHELF. It used to hold every settled
 * season behind it (~2 MB stored each), so every mid-season save re-sent the
 * whole history. It now carries only `seasons` — which seasons, by number and
 * seed — and the seasons themselves are rows of their own, written once.
 */

import type { DriverCard } from '../domain/types.ts';
import type { Team } from '../domain/field.ts';
import type { SeasonRaceResult, SeasonState } from '../domain/season.ts';
import { SEASON_RACES } from '../domain/schedule.ts';
import type { RaceFinanceResult } from '../domain/finance/raceFinance.ts';

// ---------------------------------------------------------------------------
// The season on the workbench
// ---------------------------------------------------------------------------

/**
 * A CAREER IN PROGRESS, exactly as `App.tsx` holds it.
 *
 * `state` is the only part that moves. The other three are fixed when the season
 * begins and are exactly what the winter reads at the end of it: the licensed
 * roster the season was RUN with (not the re-rated one), the garage that entered
 * it — with liveries, which `SeasonState.teams` does not carry — and the
 * seasons-run counter carried out of the previous winter.
 *
 * Structurally identical to `App.tsx`'s own `CareerLine`, and imported by it, so
 * "the thing on the workbench" and "the thing that gets saved" cannot drift into
 * two different shapes.
 */
export interface LiveSeason {
  /**
   * D-EZ-99 — which league this season belongs to. Optional only because a
   * season begun before league ids existed has none; the shell always sets it,
   * and settling refuses to fold a season into a different league's shelf.
   */
  leagueId?: string;
  seasonNumber: number;
  roster: DriverCard[];
  teams: Team[];
  seasonsRun?: Record<string, number>;
  state: SeasonState;
}

// ---------------------------------------------------------------------------
// What goes in the slot row, and what goes in the chunk rows
// ---------------------------------------------------------------------------

/**
 * A SETTLED SEASON, NAMED — what a head lists instead of carrying the season.
 * The seed is there so a stored season row can be checked against the head that
 * names it: a row with the right number and the wrong seed is a different year.
 */
export interface SeasonRef {
  seasonNumber: number;
  seed: number;
}

/**
 * THE HEAD — everything about a saved season that is not per-race history.
 *
 * 46-65 KB of JSON, 6-8 KB gzipped, at every point in the year. It is written
 * on every save and it is the COMMIT POINT: `racesRun` is what says how much of
 * the season the slot claims, and a load refuses unless exactly that many chunks
 * are there. So a half-finished save leaves the previous head in place and is
 * invisible rather than half-loadable.
 *
 * `state` here is a `SeasonState` with its four accumulating arrays emptied. It
 * is deliberately the REAL type rather than an `Omit<>`: every other field must
 * survive untouched, and a structural type that listed them would have to be
 * edited every time `seasonPlay.ts` grows a field — which is the failure mode
 * where a new carry field is silently not saved and race 15 comes back subtly
 * wrong. Emptying named arrays cannot forget a field it does not name.
 */
export interface SeasonSlotHead {
  /** Format version. Set by `leagueSlot.ts`, which owns the number. */
  v: number;
  /** Discriminates this payload from a finished-career one in the same column. */
  kind: 'season';
  /** D-EZ-99 — the league this season and every season it names belong to. */
  leagueId: string;
  leagueName: string;
  /** Which season of the career this is. Season 1 of a new league is 1. */
  seasonNumber: number;
  seed: number;
  /** `SeasonState.seasonId` — copied out so a chunk row can be matched to it. */
  seasonId: string;
  /** See `seasonKeyFor`. Identifies this season INSTANCE, not this seed. */
  seasonKey: string;
  /** Races in the books. Exactly this many chunks must be stored. */
  racesRun: number;
  /** The licensed roster as the season BEGAN. The winter re-rates from this. */
  roster: DriverCard[];
  /** The garage, with liveries. */
  teams: Team[];
  /** Carried out of the previous winter. Empty for the first season of a league. */
  seasonsRun: Record<string, number>;
  /**
   * D-EZ-99 — THE SETTLED SEASONS BEHIND THIS ONE, by number and seed, oldest
   * first. The seasons themselves are stored once each, beside the head (see
   * `leagueSlot.ts`), and a load refuses unless every one named here is there.
   */
  seasons: SeasonRef[];
  /** The season state with `races`, `standingsAfterRace`, `events` and the
   *  finance track's three accumulating arrays emptied. */
  state: SeasonState;
}

/**
 * ONE FINISHED RACE — the unit of storage, and the unit of a save.
 *
 * 2.4-4.1 MB of JSON, 205-338 KB gzipped. Immutable the moment the checkered
 * flag falls: nothing in `runSeasonRace` ever writes to a race that is already
 * in `state.races`. That immutability is what makes an incremental save correct
 * rather than merely convenient — a chunk that is already stored is already
 * right, so it is never re-uploaded and never re-checked.
 *
 * `standings` is NOT a field here because `races[i].standings` already is it.
 * `finance` is null exactly when the season is being played without the books.
 */
export interface SeasonRaceChunk {
  /** 1-based, and equal to `race.raceNumber`. Kept at the top level because it
   *  is the row's key and a reader should not have to parse 3 MB to find it. */
  raceNumber: number;
  race: SeasonRaceResult;
  finance: RaceFinanceResult | null;
}

export interface SeasonSlotSplit {
  head: SeasonSlotHead;
  /** Ascending by race number, 1..racesRun, no gaps. */
  chunks: SeasonRaceChunk[];
}

/** A refusal. One sentence, aimed at whoever has to act on it. */
export interface SeasonSlotProblem {
  ok: false;
  problem: string;
}

// ---------------------------------------------------------------------------
// Naming a season instance
// ---------------------------------------------------------------------------

/**
 * Two 32-bit FNV-1a passes over the same text with different offset bases, hex
 * joined — 64 bits of fingerprint.
 *
 * NOT A SECURITY FUNCTION and not a checksum of the save. It exists only to
 * separate two seasons that would otherwise collide on a key, and a collision
 * is caught downstream by the integrity checks in `rebuildSeasonState` rather
 * than trusted away here. Written out rather than imported because the whole
 * thing is nine lines and a dependency for nine lines is a dependency too many.
 */
function fingerprint(text: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x85ebca6b) >>> 0;
  }
  return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

/**
 * THE KEY THE CHUNK ROWS ARE FILED UNDER, and the reason it is not `seasonId`.
 *
 * `beginSeason` defaults `seasonId` to `season-${seed}`, so TWO DIFFERENT
 * SEASONS CAN SHARE ONE. Season 4 of a league is played on a seed derived from
 * the league's own, off a roster the winter re-rated; a brand-new league started
 * on that same number is a different season with a different roster and the same
 * `seasonId`. Filing chunks by `seasonId` alone would let race 3 of one be
 * loaded as race 3 of the other — a plausible wrong season, which is the exact
 * class of result this project refuses to print.
 *
 * So the key fingerprints everything that FIXES a season before it is played:
 * the id, the seed, which season of the career it is, the calendar, and the
 * starting roster and garage. None of those changes while the season is running,
 * which is what lets a save at race 20 recognise the chunks a save at race 14
 * wrote — from a different browser, on a different day.
 */
export function seasonKeyFor(live: LiveSeason): string {
  const facts = JSON.stringify({
    // D-EZ-99 — two leagues never share a season's race rows, even on one seed.
    leagueId: live.leagueId ?? null,
    seasonId: live.state.seasonId,
    seed: live.state.seed,
    seasonNumber: live.seasonNumber,
    schedule: live.state.schedule,
    roster: live.roster,
    teams: live.teams,
  });
  return `${live.state.seasonId}-s${live.seasonNumber}-${fingerprint(facts)}`;
}

// ---------------------------------------------------------------------------
// Splitting
// ---------------------------------------------------------------------------

/**
 * Turn the season on the workbench into a head and one chunk per finished race.
 *
 * PURE, and it does not touch the state it is given: the head is a fresh object
 * whose arrays are new empty ones, and the chunks hold the SAME race objects the
 * state holds. Sharing them is safe for the same reason `hydrate` shares them —
 * nothing in the engine ever writes to a finished race — and it means splitting
 * a 138 MB season costs one shallow copy rather than a second 138 MB.
 */
export function splitSeasonSave(
  live: LiveSeason,
  leagueName: string,
  formatVersion: number,
  seasons: readonly SeasonRef[] = [],
  leagueId: string = live.leagueId ?? '',
): SeasonSlotSplit {
  const { state } = live;
  const chunks: SeasonRaceChunk[] = state.races.map((race, i) => ({
    raceNumber: race.raceNumber,
    race,
    // Index, not a lookup by race number: the finance track appends one result
    // per race in the same order, so `races[i]` and `finance.races[i]` are the
    // same weekend. `rebuildSeasonState` checks that they agree.
    finance: state.finance ? (state.finance.races[i] ?? null) : null,
  }));

  return {
    head: {
      v: formatVersion,
      kind: 'season',
      leagueId,
      leagueName: leagueName.trim() || 'RASCAL',
      seasonNumber: live.seasonNumber,
      seed: state.seed,
      seasonId: state.seasonId,
      seasonKey: seasonKeyFor(live),
      seasons: seasons.map((s) => ({ seasonNumber: s.seasonNumber, seed: s.seed })),
      racesRun: state.races.length,
      roster: live.roster,
      teams: live.teams,
      seasonsRun: live.seasonsRun ?? {},
      state: {
        ...state,
        races: [],
        standingsAfterRace: [],
        events: [],
        finance: state.finance
          ? { ...state.finance, events: [], races: [], sponsorChanges: [] }
          : null,
      },
    },
    chunks,
  };
}

// ---------------------------------------------------------------------------
// Reading a head back off a row
// ---------------------------------------------------------------------------

/**
 * Refuse anything we cannot honestly claim to understand.
 *
 * Every branch is a real way a stored head goes wrong: a truncated write, a row
 * from before a field existed, a career payload read down the season path. The
 * alternative — trusting the JSON and letting `runSeasonRace` throw twelve
 * frames deep on `undefined.driverId` at race 15 — turns a storage problem into
 * what looks like a rules bug, which is the most expensive kind to chase.
 */
export function readSeasonSlotHead(raw: unknown): SeasonSlotHead | SeasonSlotProblem {
  if (raw === null || typeof raw !== 'object') {
    return { ok: false, problem: 'the stored season is not an object at all' };
  }
  const p = raw as Partial<SeasonSlotHead>;

  if (p.kind !== 'season') {
    return {
      ok: false,
      problem: `the slot says it holds a season in progress but the stored payload is marked "${String(p.kind ?? 'nothing')}"`,
    };
  }

  const missing = [
    !p.state || typeof p.state !== 'object' ? 'the season state' : null,
    !Array.isArray(p.roster) ? "the season's starting roster" : null,
    !Array.isArray(p.teams) ? 'the garage' : null,
    !Array.isArray(p.state?.schedule?.races) ? 'the calendar' : null,
    !Array.isArray(p.state?.carry?.feuds) ? 'the carry between races' : null,
    typeof p.seasonNumber !== 'number' ? 'the season number' : null,
    typeof p.racesRun !== 'number' ? 'the number of races run' : null,
    typeof p.seasonKey !== 'string' || !p.seasonKey ? 'the key its races are filed under' : null,
    typeof p.leagueId !== 'string' || !p.leagueId ? 'the league it belongs to' : null,
    !Array.isArray(p.seasons) ? 'the list of seasons behind it' : null,
  ].filter((x): x is string => x !== null);

  if (missing.length) {
    return { ok: false, problem: `it is missing ${missing.join(', ')}` };
  }

  const racesRun = p.racesRun as number;
  if (!Number.isInteger(racesRun) || racesRun < 0 || racesRun > SEASON_RACES) {
    return {
      ok: false,
      problem: `it claims ${racesRun} races run, and a season is ${SEASON_RACES} races`,
    };
  }
  // The state's own cursor and the head's count are two recordings of one fact.
  // If they disagree, one of them is wrong and there is no way to tell which.
  if (p.state!.nextRace !== racesRun + 1) {
    return {
      ok: false,
      problem: `it claims ${racesRun} races run but the season state is waiting on race ${p.state!.nextRace}`,
    };
  }

  return {
    v: typeof p.v === 'number' ? p.v : 0,
    kind: 'season',
    leagueId: p.leagueId as string,
    leagueName: typeof p.leagueName === 'string' && p.leagueName.trim() ? p.leagueName : 'RASCAL',
    seasonNumber: p.seasonNumber as number,
    seed: typeof p.seed === 'number' ? p.seed : p.state!.seed,
    seasonId: typeof p.seasonId === 'string' && p.seasonId ? p.seasonId : p.state!.seasonId,
    seasonKey: p.seasonKey as string,
    racesRun,
    roster: p.roster as DriverCard[],
    teams: p.teams as Team[],
    seasonsRun: (p.seasonsRun ?? {}) as Record<string, number>,
    seasons: (p.seasons as SeasonRef[]).map((s) => ({ seasonNumber: Number(s?.seasonNumber), seed: Number(s?.seed) })),
    state: p.state as SeasonState,
  };
}

/** The same discipline for one chunk. `expected` is the race number the row was filed under. */
export function readSeasonRaceChunk(raw: unknown, expected: number): SeasonRaceChunk | SeasonSlotProblem {
  if (raw === null || typeof raw !== 'object') {
    return { ok: false, problem: `race ${expected} is not stored as an object` };
  }
  const c = raw as Partial<SeasonRaceChunk>;
  if (!c.race || typeof c.race !== 'object') {
    return { ok: false, problem: `race ${expected} has no race record in it` };
  }
  if (!Array.isArray(c.race.events)) {
    return { ok: false, problem: `race ${expected} has no event slice` };
  }
  if (!c.race.standings || !Array.isArray(c.race.standings.drivers)) {
    // The standings are load-bearing twice over: they are the race's own record
    // AND `state.standingsAfterRace[i]`, which `finishSeason` reads for the
    // final table. A chunk without them would resume and then report a season
    // with no points table, which reads as a rules bug.
    return { ok: false, problem: `race ${expected} has no standings` };
  }
  if (c.race.raceNumber !== expected) {
    return {
      ok: false,
      problem: `the row filed as race ${expected} contains race ${String(c.race.raceNumber)}`,
    };
  }
  return {
    raceNumber: expected,
    race: c.race as SeasonRaceResult,
    finance: (c.finance ?? null) as RaceFinanceResult | null,
  };
}

// ---------------------------------------------------------------------------
// Rebuilding
// ---------------------------------------------------------------------------

/**
 * PUT THE SEASON BACK TOGETHER, or refuse to.
 *
 * The three derived arrays are rebuilt here, and every one of them is rebuilt in
 * the order and the key order `runSeasonRace` produced it, because "the resumed
 * season is EXACTLY the season that was saved" is a claim about bytes and not
 * about vibes. `scripts/slot-resume.ts` compares the output of this function to
 * the state that went in, canonical-JSON to canonical-JSON, and then plays both
 * out to race 36 and compares the reports.
 *
 * THE INTEGRITY GATE, AND WHY IT IS WORTH ITS COST.
 *
 * A chunk set can be wrong in ways no field-by-field validation would notice:
 * one race missing from the middle, or — if two seasons ever collided on a
 * `seasonKey` — a race from a different career sitting in the right slot. Both
 * would rebuild into a season that RUNS, and produce a champion, a bracket and a
 * full set of books that were never played. So three cheap structural facts are
 * checked, and each one is an invariant of `runSeasonRace` rather than a
 * convention:
 *
 *   • the chunks are exactly races 1..racesRun, in order;
 *   • every event in the rebuilt stream belongs to this season id, and the
 *     stream's `sequence` numbers run 0, 1, 2, … with no gap — which follows
 *     from `state.sequence` counting emitted events and every emitted event
 *     landing in the stream. A missing or foreign race breaks it immediately.
 *   • `finance.races[i]` is the weekend `races[i]` was.
 *
 * Together those make a mixed-up chunk set a refusal instead of a season.
 */
export function rebuildSeasonState(
  head: SeasonSlotHead,
  chunks: readonly SeasonRaceChunk[],
): { ok: true; state: SeasonState } | SeasonSlotProblem {
  if (chunks.length !== head.racesRun) {
    return {
      ok: false,
      problem: `it says ${head.racesRun} race${head.racesRun === 1 ? '' : 's'} were run but ${chunks.length} ${chunks.length === 1 ? 'is' : 'are'} stored`,
    };
  }
  for (const [i, chunk] of chunks.entries()) {
    if (chunk.raceNumber !== i + 1) {
      return {
        ok: false,
        problem: `the stored races are out of order or incomplete — race ${i + 1} is missing (found ${chunk.raceNumber} in its place)`,
      };
    }
  }

  const wantsFinance = head.state.finance !== null;
  for (const chunk of chunks) {
    if (wantsFinance && !chunk.finance) {
      return {
        ok: false,
        problem: `the season keeps books but race ${chunk.raceNumber} has no money in it`,
      };
    }
    if (!wantsFinance && chunk.finance) {
      return {
        ok: false,
        problem: `race ${chunk.raceNumber} carries money for a season that is not keeping books`,
      };
    }
    if (chunk.finance && chunk.finance.raceNumber !== chunk.raceNumber) {
      return {
        ok: false,
        problem: `race ${chunk.raceNumber}'s money is filed as race ${chunk.finance.raceNumber}`,
      };
    }
  }

  const races = chunks.map((c) => c.race);
  // `standingsAfterRace[i]` IS `races[i].standings` — the same object in the
  // engine, so the same object here. See the header.
  const standingsAfterRace = races.map((r) => r.standings);
  const events = races.flatMap((r) => r.events);

  // ---- the gate ---------------------------------------------------------
  if (events.length !== head.state.sequence) {
    return {
      ok: false,
      problem:
        `the stored races hold ${events.length} ledger entries and the season counted ${head.state.sequence}` +
        ' — one of the races is not the race it should be',
    };
  }
  for (const [i, event] of events.entries()) {
    if (event.sequence !== i) {
      return {
        ok: false,
        problem: `the season ledger jumps at entry ${i} (it is numbered ${event.sequence}) — the stored races do not fit together`,
      };
    }
    if (event.seasonId !== head.state.seasonId) {
      return {
        ok: false,
        problem: `ledger entry ${i} belongs to season "${event.seasonId}" and this slot holds "${head.state.seasonId}"`,
      };
    }
  }

  const finance = head.state.finance
    ? {
        ...head.state.finance,
        races: chunks.map((c) => c.finance as RaceFinanceResult),
        events: chunks.flatMap((c) => (c.finance as RaceFinanceResult).events),
        // The race number is stamped on here in the same expression order
        // `runSeasonRace` uses (`{ ...change, raceNumber }`), so the rebuilt
        // object's KEY ORDER matches and the JSON is byte-identical. A
        // difference here would be invisible to a deep-equal and loud in the
        // byte comparison the harness makes.
        sponsorChanges: chunks.flatMap((c) =>
          (c.finance as RaceFinanceResult).sponsorChanges.map((change) => ({
            ...change,
            raceNumber: c.raceNumber,
          })),
        ),
      }
    : null;

  return {
    ok: true,
    state: {
      ...head.state,
      races,
      standingsAfterRace,
      events,
      finance,
    },
  };
}

/** The season, ready for the workbench. `state` is the rebuilt one. */
export function openSeasonSlot(head: SeasonSlotHead, state: SeasonState): LiveSeason {
  return {
    leagueId: head.leagueId,
    seasonNumber: head.seasonNumber,
    roster: head.roster,
    teams: head.teams,
    seasonsRun: head.seasonsRun,
    state,
  };
}

